import db from "../db/database";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { marcaConSolape } from "./margenDeSolape";
import { isOnline } from "./syncService";
import { anotarAviso, anotarError, anotarLectura, anotarSinNovedades } from "./syncLog";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "líneas de producto";

// Función auxiliar para normalizar nombres de propiedades
const normalizeFieldNames = (bcLine) => {
  // Verificar cada propiedad para manejar diferentes formatos de nombres
  const normalized = {};

  // Normalizar propiedades principales
  normalized.No = bcLine.No || "";
  normalized.Parent_Item_No = bcLine["Parent_Item_No"] || bcLine["Parent Item No."] || "";

  // Detectar y normalizar Line_No (podría venir como "Line_No" o "Line No.")
  if (bcLine["Line_No"] !== undefined) {
    normalized.Line_No = bcLine["Line_No"];
  } else if (bcLine["Line No."] !== undefined) {
    normalized.Line_No = bcLine["Line No."];
  } else {
    normalized.Line_No = 0;
  }

  // Normalizar otros campos
  normalized.Type = bcLine.Type || "";
  normalized.Description = bcLine.Description || "";
  normalized.Source_Counter = bcLine.Source_Counter || 0;

  // Detectar y normalizar Quantity_per
  if (bcLine["Quantity_per"] !== undefined) {
    normalized.Quantity_per = bcLine["Quantity_per"];
  } else if (bcLine["Quantity per"] !== undefined) {
    normalized.Quantity_per = bcLine["Quantity per"];
  } else {
    normalized.Quantity_per = 0;
  }

  // Detectar y normalizar Unit_of_Measure_Code
  if (bcLine["Unit_of_Measure_Code"] !== undefined) {
    normalized.Unit_of_Measure_Code = bcLine["Unit_of_Measure_Code"];
  } else if (bcLine["Unit of Measure Code"] !== undefined) {
    normalized.Unit_of_Measure_Code = bcLine["Unit of Measure Code"];
  } else {
    normalized.Unit_of_Measure_Code = "";
  }

  // Normalizar Variant_Code
  if (bcLine["Variant_Code"] !== undefined) {
    normalized.Variant_Code = bcLine["Variant_Code"];
  } else if (bcLine["Variant Code"] !== undefined) {
    normalized.Variant_Code = bcLine["Variant Code"];
  } else {
    normalized.Variant_Code = "";
  }
  return normalized;
};

// Obtener todas las líneas para un producto padre específico
export const getProductLines = async (parentItemNo) => {
  try {
    const productLines = await db.productLines
      .where("Parent_Item_No")
      .equals(parentItemNo)
      .and((line) => !line.deleted)
      .toArray();

    return productLines;
  } catch (error) {
    anotarError(ENTIDAD, `no se pudieron leer las del producto ${parentItemNo}`, error);
    throw error;
  }
};

// Sincronizar líneas de producto desde Business Central
/**
 * Borra todas las líneas de producto locales. Se llama al cerrar caja, junto
 * con clearProductsForLocation: las líneas describen la composición de los
 * productos de la tienda, así que no tiene sentido dejarlas vivas sin catálogo
 * que las respalde.
 *
 * Es seguro borrarlas: syncProductLines deriva su checkpoint incremental del
 * propio Source_Counter más alto entre las líneas ya guardadas, así que sin
 * líneas locales la próxima sincronización arranca sin filtro y BC devuelve el
 * catálogo completo de nuevo (no un delta a medias). Mismo razonamiento que
 * clearProductsForLocation.
 */
export const clearProductLines = async () => {
  await db.productLines.clear();
};

export const syncProductLines = async () => {
  if (!isOnline()) {
    return false;
  }

  try {
    const localProductsLines = await db.productLines.toArray();

    // Con colchón, y sin `sort` restando: ver `margenDeSolape.js`. Un
    // contador que retrocede deja la fila por debajo de la marca y no vuelve
    // nunca, y un contador ausente convertía el comparador en `NaN`.
    const lastSourceCode = marcaConSolape(localProductsLines);

    const response = await fetchFromBusinessCentral(
      "Kits" + (lastSourceCode ? `?$filter=Source_Counter gt ${lastSourceCode}` : "")
    );

    // Verificar que la respuesta sea un array
    if (!response || !Array.isArray(response)) {
      anotarError(ENTIDAD, "la respuesta de Business Central no es un arreglo", response);
      return false;
    }

    const bcProductLines = response;

    if (bcProductLines.length === 0) {
      anotarSinNovedades(ENTIDAD, Boolean(lastSourceCode));
      return true;
    }

    // Crear mapa de líneas locales para búsqueda rápida
    const localLinesByKey = {};
    localProductsLines.forEach((line) => {
      const key = `${line.Parent_Item_No}-${line.Line_No}-${line.No}`;
      localLinesByKey[key] = line;
    });

    // Se arma todo el lote y se escribe de una vez. Antes era un add/update
    // secuencial por línea: con 32.324 líneas en BC (y el tope de página de
    // 20.000 ya superado) eran decenas de miles de transacciones sueltas a
    // IndexedDB, que es lo que hacía parecer que la sincronización no traía
    // nada -- terminaba en pantalla mientras el guardado seguía corriendo.
    const records = [];
    let omitidas = 0;

    for (const bcLine of bcProductLines) {
      // Normalizar nombres de propiedades
      const normalizedLine = normalizeFieldNames(bcLine);

      // Verificar que los campos obligatorios existan
      if (!normalizedLine.No || !normalizedLine.Parent_Item_No) {
        omitidas += 1;
        continue;
      }

      const key = `${normalizedLine.Parent_Item_No}-${normalizedLine.Line_No}-${normalizedLine.No}`;
      const existingLine = localLinesByKey[key];

      if (existingLine) {
        // Una línea con cambios locales sin sincronizar no se pisa con BC.
        if (existingLine.pendingSync !== 0) continue;

        // bulkPut con el id existente actualiza en vez de duplicar.
        records.push({
          ...existingLine,
          ...normalizedLine,
          id: existingLine.id,
          lastModified: new Date(),
          lastSynced: new Date(),
          pendingSync: 0,
          deleted: false,
        });
      } else {
        records.push({
          ...normalizedLine,
          lastSynced: new Date(),
          pendingSync: 0,
          deleted: false,
        });
      }
    }

    if (records.length > 0) {
      await db.productLines.bulkPut(records);
    }

    anotarLectura(ENTIDAD, {
      guardados: records.length,
      incremental: Boolean(lastSourceCode),
      marca: lastSourceCode || undefined,
    });

    if (omitidas > 0) {
      anotarAviso(ENTIDAD, `${omitidas} omitidas por venir sin No o sin Parent_Item_No`);
    }
    return true;
  } catch (error) {
    anotarError(ENTIDAD, "falló la lectura", error);
    return false;
  }
};

// Verificar si un producto tiene líneas asociadas
export const hasProductLines = async (productNo) => {
  try {
    const count = await db.productLines
      .where("Parent_Item_No")
      .equals(productNo)
      .and((line) => !line.deleted)
      .count();

    return count > 0;
  } catch (error) {
    anotarError(ENTIDAD, `no se pudo comprobar si el producto ${productNo} tiene`, error);
    return false;
  }
};
